import { DateTime } from "luxon";
import React from "react";
import SaveBtn from "../../../../common/SaveBtn";
import tools from "../../../../../helpers/tools";
import Control from "../../../../common/Control";
import { useEpSession } from "../EpSessionContext";
import { colors, gradients, typography, iconBox } from "../theme";

const findMemberName = (presentJuryMembers, userId) => {
  const member = presentJuryMembers.find((j) => j.userManager.id === userId);
  return member ? tools.getFullname(member.userManager) : "—";
};

const EpSessionInfoBar = ({
  canModifyWriters,
  showRolesEditor,
  setShowRolesEditor,
  writerPresident,
  setWriterPresident,
  saveWriterPresident,
  isSavingWriters,
  presentJuryMembers,
}) => {
  const { ep } = useEpSession();
  const presidentName = findMemberName(presentJuryMembers, ep.president);
  const writerName = findMemberName(presentJuryMembers, ep.writer);

  return (
    <>
      {/* EP Info bar */}
      <div
        data-tour="info-bar"
        className="rounded mb-3"
        style={{
          background: colors.bg.white,
          border: `1px solid ${colors.border.default}`,
          overflow: "hidden",
        }}
      >
        <div className="d-flex flex-wrap">
          {/* Date */}
          <div
            className="d-flex align-items-center p-3 flex-grow-1"
            style={{ borderRight: `1px solid ${colors.border.light}`, gap: 10 }}
          >
            <div style={iconBox(36, gradients.indigo)}>
              <i
                className="fa fa-calendar"
                style={{ fontSize: 14, color: colors.text.white }}
              />
            </div>
            <div>
              <div style={typography.label}>Date de la session</div>
              <div
                style={{
                  ...typography.value,
                  textTransform: "capitalize",
                }}
              >
                {DateTime.fromISO(ep.beginAt, { zone: "UTC" })
                  .toLocal()
                  .set({
                    hour: ep.beginHourAt,
                    minute: ep.beginMinuteAt,
                    second: 0,
                  })
                  .setLocale("fr")
                  .toFormat("EEEE dd MMMM yyyy — HH'h'mm")}
              </div>
            </div>
          </div>
          {/* Secteur */}
          <div
            className="d-flex align-items-center p-3"
            style={{
              borderRight: `1px solid ${colors.border.light}`,
              gap: 10,
              minWidth: 140,
            }}
          >
            <div style={iconBox(36, gradients.sky)}>
              <i
                className="fa fa-map-marker"
                style={{ fontSize: 14, color: colors.text.white }}
              />
            </div>
            <div>
              <div style={typography.label}>Secteur</div>
              <div style={typography.value}>
                {ep.sector?.name || ep.sector || "—"}
              </div>
            </div>
          </div>
          {/* Président */}
          <div
            className="d-flex align-items-center p-3"
            onClick={() =>
              !ep.closedAt && canModifyWriters && setShowRolesEditor((v) => !v)
            }
            style={{
              borderRight: `1px solid ${colors.border.light}`,
              gap: 10,
              cursor: !ep.closedAt && canModifyWriters ? "pointer" : "default",
              transition: "background 0.15s ease",
              background: showRolesEditor ? colors.bg.page : "transparent",
            }}
          >
            <div style={iconBox(36, gradients.warning)}>
              <i
                className="fa fa-gavel"
                style={{ fontSize: 14, color: colors.text.white }}
              />
            </div>
            <div>
              <div style={typography.label}>Président de séance</div>
              <div className="d-flex align-items-center" style={{ gap: 6 }}>
                <span
                  style={{
                    ...typography.value,
                    color:
                      !ep.closedAt && canModifyWriters
                        ? colors.text.link
                        : colors.text.primary,
                  }}
                >
                  {presidentName}
                </span>
                {!ep.closedAt && canModifyWriters && (
                  <i
                    className="fa fa-pencil"
                    style={{ fontSize: 10, color: colors.primary.light }}
                  />
                )}
              </div>
            </div>
          </div>
          {/* Secrétaire */}
          <div
            className="d-flex align-items-center p-3"
            onClick={() =>
              !ep.closedAt && canModifyWriters && setShowRolesEditor((v) => !v)
            }
            style={{
              gap: 10,
              cursor: !ep.closedAt && canModifyWriters ? "pointer" : "default",
              transition: "background 0.15s ease",
              background: showRolesEditor ? colors.bg.page : "transparent",
            }}
          >
            <div style={iconBox(36, gradients.success)}>
              <i
                className="fa fa-edit"
                style={{ fontSize: 14, color: colors.text.white }}
              />
            </div>
            <div>
              <div style={typography.label}>Secrétaire</div>
              <div className="d-flex align-items-center" style={{ gap: 6 }}>
                <span
                  style={{
                    ...typography.value,
                    color:
                      !ep.closedAt && canModifyWriters
                        ? colors.text.link
                        : colors.text.primary,
                  }}
                >
                  {writerName}
                </span>
                {!ep.closedAt && canModifyWriters && (
                  <i
                    className="fa fa-pencil"
                    style={{ fontSize: 10, color: colors.primary.light }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modification rédacteur/président */}
      {!ep.closedAt && canModifyWriters && showRolesEditor && (
        <div
          className="rounded mb-3 p-3"
          style={{
            background: colors.bg.white,
            border: `1px solid ${colors.border.default}`,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div className="d-flex align-items-center mb-3" style={{ gap: 10 }}>
            <div style={iconBox(28, gradients.primary)}>
              <i
                className="fa fa-sync"
                style={{ fontSize: 12, color: colors.text.white }}
              />
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: colors.text.primary,
              }}
            >
              Modifier les rôles
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-5">
              <Control
                label="Président de séance"
                name="president"
                type="select"
                datas={presentJuryMembers.map((j) => ({
                  name: tools.getFullname(j.userManager),
                  ...j.userManager,
                }))}
                dataIndex="id"
                dataLabel="name"
                value={writerPresident.president}
                change={(e) =>
                  setWriterPresident({
                    ...writerPresident,
                    president: e.target.value,
                  })
                }
              />
            </div>
            <div className="col-12 col-md-5">
              <Control
                label="Rédacteur(trice)"
                name="writer"
                type="select"
                datas={presentJuryMembers.map((j) => ({
                  name: tools.getFullname(j.userManager),
                  ...j.userManager,
                }))}
                dataIndex="id"
                dataLabel="name"
                value={writerPresident.writer}
                change={(e) =>
                  setWriterPresident({
                    ...writerPresident,
                    writer: e.target.value,
                  })
                }
              />
            </div>

            <div className="col-12 col-md-2 d-flex align-items-end pb-2">
              <SaveBtn
                margin="mt-0 mb-2"
                className=""
                text="Valider"
                save={saveWriterPresident}
                isSaving={isSavingWriters}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EpSessionInfoBar;
